import {
    useState,
    useEffect,
    FC,
    ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { getDrivers } from '../../../api/drivers.api';
import { startUpdateWorkspace, useWorkspaceStore } from '../../../store/workspace.store';
import { IDriver, IDriversQuery } from '../../../interfaces/driver.interface';
import { DriversContext } from '../context/drivers.ctx';

export type SearchParamValue = string | string[] | boolean | number | null | undefined | Date;

const DriversProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const PAGE = 1;
    const LIMIT = 10;

    const workspace = useWorkspaceStore((state) => state.data);

    const [drivers, setDrivers] = useState<IDriver[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [pagination, setPagination] = useState<{ limit: number; page: number; }>({
        limit: LIMIT,
        page: PAGE,
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [driver, setDriver] = useState<string | null>(null);
    const [openAddDriver, setOpenAddDriver] = useState<boolean>(false);

    const fetchData = () => {
        setLoading(true);

        const params: IDriversQuery = {
            limit: pagination.limit,
            page: pagination.page,
            driverIds: workspace?.drivers || [],
        };

        const search = searchParams.get('search');
        if (search) {
            params.search = search;
        }

        getDrivers(params)
            .then(({ data: response }) => {
                setTotal(response.total);
                setDrivers(response.data);
            })
            .catch((error: any) => {
                setErrorMessage(error.message);
            })
            .finally(() => setLoading(false));
    };

    const handleSearchParam = (key: string, value: SearchParamValue) => {
        if (value === null || value === undefined || (Array.isArray(value) && value.length === 0) || value === '') {
            searchParams.delete(key);
        } else if (Array.isArray(value)) {
            searchParams.set(key, JSON.stringify(value));
        } else if (value instanceof Date) {
            searchParams.set(key, value.toISOString());
        } else {
            searchParams.set(key, value.toString());
        }

        searchParams.set('page', '1');

        setPagination({
            ...pagination,
            page: 1,
        });

        setSearchParams(searchParams);
    };

    const handleSetDriver = (driverID: string, data: any) => {
        const newDriverData = drivers.map((d: any) => {
            if (d?._id === driverID) {
                return { ...d, ...data };
            }
            return d;
        });

        setDrivers(newDriverData);
    };

    const handleRemoveDriver = (driverID: string) => {
        const newListDrivers = workspace?.drivers.filter((d: string) => d.toString() !== driverID);

        setDrivers((prevDrivers: any) => prevDrivers.filter((d: any) => d?._id !== driverID));
        startUpdateWorkspace({ drivers: newListDrivers });
        setTotal(newListDrivers?.length || 0);
    };

    const handleAddDriver = (driverID: string) => {
        const newListDrivers = [...(workspace?.drivers || []), driverID];
        startUpdateWorkspace({ drivers: newListDrivers });
        setTotal(newListDrivers?.length || 0);
    };

    useEffect(() => {
        fetchData();
    }, [searchParams, pagination.page]);

    return (
        <DriversContext.Provider
            value={{
                drivers,
                driver,
                pagination,
                total,
                loading,
                errorMessage,
                fetchData,
                handleSetDriver,
                handleRemoveDriver,
                setPagination,
                handleSearchParam,
                setDriver,
                setOpenAddDriver,
                openAddDriver,
                handleAddDriver,
            }}
        >
            {children}
        </DriversContext.Provider>
    );
};

export { DriversProvider };
